import React, { useState, useCallback, useMemo } from "react";
import { Flex, Text, Button, Link } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { DocumentUploadInviteDetails } from "../../../graphql/supergraphTypes";
import { formatDate } from "../../../utils/date_formatter";
import { DefaultModal } from "../../common/DefaultModal";
import { DocumentFormProps } from "../DocumentCollector/types";
import { DocumentLists } from "../DocumentLists";
import { DocumentDetailsType, DocumentListProps } from "../DocumentLists/types";
import { DocumentUploader } from "../DocumentUploader";
import { InviteToUploadModal } from "../InviteToUploadModal";
import { getDocumentStatus } from "../utils";
import { DocumentDetailsProps } from "./types";

export const DocumentDetails: React.FC<DocumentDetailsProps> = ({
  caseId,
  profileId,
  profileName,
  documentDetails,
  updateDocument,
  onCompletion,
  isSubmitting,
  allDocumentsComplete,
  canShowInvite = false,
  disableInviteButton = false,
  onInviteSent,
  statusBanner,
  expiredDocumentsBanner,
}) => {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showMissingDocModal, setShowMissingDocModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentDetailsType | null>(null);

  const handleBackToList = useCallback(() => {
    setSelectedDocument(null);
  }, [setSelectedDocument]);

  const handleSave = useCallback(
    (document: DocumentFormProps) => {
      const { id, file, uploaderInfo } = document;

      // Case 1: New file being added (no existing ID)
      if (!id && file) {
        updateDocument(handleBackToList, document, null);
        return;
      }

      // Case 2: Existing file is replaced (has ID and a new file without a URL)
      if (id && file && !file.url) {
        updateDocument(handleBackToList, document, id);
        return;
      }

      // Case 3: Existing file is deleted (has ID, no file, and no uploaderInfo)
      if (id && !file && !uploaderInfo) {
        updateDocument(handleBackToList, null, id);
        return;
      }
    },
    [updateDocument, handleBackToList],
  );

  const handleOpenInviteModal = useCallback(() => {
    setShowInviteModal(true);
  }, []);

  const handleCloseInviteModal = useCallback(
    (inviteDetails: DocumentUploadInviteDetails | null) => {
      setShowInviteModal(false);
      if (inviteDetails && onInviteSent) {
        onInviteSent();
      }
    },
    [onInviteSent],
  );

  const documentListProps: DocumentListProps = useMemo(
    () => ({
      documentDetails: documentDetails.map((doc) => ({
        ...doc,
        lastUploadedDate: doc.lastUploadedDate ? formatDate(doc.lastUploadedDate) : undefined,
        status: getDocumentStatus(!!doc.file, doc.expiryDate, doc.uploaderInfo),
        onClick: () => setSelectedDocument(doc),
      })),
    }),
    [documentDetails],
  );

  const handleGoBack = useCallback(() => {
    if (allDocumentsComplete) {
      onCompletion();
    } else {
      setShowMissingDocModal(true);
    }
  }, [allDocumentsComplete, onCompletion]);

  return (
    <>
      {selectedDocument ? (
        <DocumentUploader
          backNavText={`Documents for ${profileName}`}
          document={selectedDocument}
          isSubmitting={isSubmitting}
          onNavigateBack={handleBackToList}
          onSave={handleSave}
        />
      ) : (
        <Flex flexDirection="column" gap={Sizes.LG}>
          <Link
            to=""
            icon="arrow-left"
            iconPosition="left"
            onClick={handleGoBack}
            variant="muted"
            align="left"
          >
            {profileName}
          </Link>

          <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
            <Text variant="h3">Documents for {profileName}</Text>
            {canShowInvite && (
              <Button
                variant="neutral"
                icon="share"
                iconPosition="left"
                onClick={handleOpenInviteModal}
                disabled={disableInviteButton}
              >
                Invite to complete
              </Button>
            )}
          </Flex>

          <Text variant="regular-md">
            Due to regulatory requirements, the following documents are required. Please verify
            the information is up to date and complete.
          </Text>

          {statusBanner}
          {expiredDocumentsBanner}

          <DocumentLists {...documentListProps} />
        </Flex>
      )}

      {canShowInvite && caseId && profileId && (
        <InviteToUploadModal
          show={showInviteModal}
          onClose={handleCloseInviteModal}
          caseId={caseId}
          profileId={profileId}
        />
      )}
      <DefaultModal
        show={showMissingDocModal}
        modalSize="small"
        title="Missing documents"
        body="Required documents haven't been uploaded yet. You can skip this step for now, but keep in mind all required documents must be submitted to complete your submission."
        negBtnText="Cancel"
        posBtnText="Continue"
        onClose={() => {
          setShowMissingDocModal(false);
        }}
        onConfirm={() => {
          onCompletion();
          setShowMissingDocModal(false);
        }}
      />
    </>
  );
};
